import {
  alpha,
  type ButtonProps,
  createTheme,
  type Interpolation,
  type Theme,
} from '@mui/material';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';

type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

const palettes: Record<'light' | 'dark', Record<ButtonColor, string>> = {
  light: {
    primary: clientThemePalette[CLIENT_THEME].light.primary,
    secondary: clientThemePalette[CLIENT_THEME].light.secondary,
    success: clientThemePalette[CLIENT_THEME].light.successButton,
    warning: clientThemePalette[CLIENT_THEME].light.warningButton,
    error: clientThemePalette[CLIENT_THEME].light.errorButton,
    info: clientThemePalette[CLIENT_THEME].light.infoButton,
  },
  dark: {
    primary: clientThemePalette[CLIENT_THEME].dark.primary,
    secondary: clientThemePalette[CLIENT_THEME].dark.secondary,
    success: clientThemePalette[CLIENT_THEME].dark.successButton,
    warning: clientThemePalette[CLIENT_THEME].dark.warningButton,
    error: clientThemePalette[CLIENT_THEME].dark.errorButton,
    info: clientThemePalette[CLIENT_THEME].dark.infoButton,
  },
};

export const getCustomLoadingButtonTheme = (mode: 'light' | 'dark') => {
  const colors = palettes[mode];

  const variants = (
    Object.entries(colors) as Array<[ButtonColor, string]>
  ).flatMap(([color, value]) => [
    {
      props: { variant: 'contained', color },
      style: {
        backgroundColor: value,
        color: '#fff',
        '&:hover': { backgroundColor: alpha(value, 0.85) },
      },
    },
    {
      props: { variant: 'outlined', color },
      style: {
        border: `1px solid ${value}`,
        color: value,
        '&:hover': { backgroundColor: alpha(value, 0.08) },
      },
    },
    {
      props: { variant: 'text', color },
      style: {
        color: value,
        '&:hover': { backgroundColor: alpha(value, 0.08) },
      },
    },
  ]) as Array<{
    props: Partial<ButtonProps<'button', unknown>>;
    style: Interpolation<{ theme: Theme }>;
  }>;

  return createTheme({
    components: {
      MuiButton: {
        variants,
        styleOverrides: {
          root: ({ ownerState }) => {
            const variant = ownerState.variant ?? 'contained';
            const color = (ownerState.color as ButtonColor) || 'primary';
            const bgColor = colors[color];

            return {
              textTransform: 'none',
              borderRadius: '8px',
              '&.Mui-disabled': {
                opacity: 0.6,
                backgroundColor:
                  variant === 'contained' ? bgColor : 'transparent',
                color: variant === 'contained' ? '#fff' : bgColor,
                borderColor: variant === 'outlined' ? bgColor : undefined,
                cursor: 'not-allowed',
              },
            };
          },
        },
      },
    },
  });
};
